// Which subject and class the board names for its period. Only .ts-suffixed runtime imports, so node --test loads it directly.

import type { Id, QltTimetableDayCell } from '../api/types';
import { vietnamDay } from '../home/date.ts';
import { groupPeriods, periodGroupLabel } from '../home/periodGroups.ts';

/** Shown when no subject is known; a real subject name here labels the period wrong. */
export const SUBJECT_FALLBACK = 'Môn học';
/** Shown when no class name is known; never the raw class id. */
export const CLASS_FALLBACK = 'Lớp học';

export interface BoardPeriod {
  date: string;
  index: number;
  classId: string;
}

export interface PeriodLabels {
  subjectTitle: string;
  classTitle: string;
}

function onDay(value: string | undefined, date: string): boolean {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) && vietnamDay(value) === date;
}

/** The timetable-day cell for exactly this period, or null. */
export function periodCell(cells: QltTimetableDayCell[], period: BoardPeriod): QltTimetableDayCell | null {
  return cells.filter((cell) => cell.index === period.index && String(cell.class.id) === period.classId && onDay(cell.date, period.date))[0] || null;
}

/** "Tiết 3-4" when this period sits in a block of the same class and subject, else "Tiết 4". */
export function periodLabel(cells: QltTimetableDayCell[], period: BoardPeriod): string {
  const day = cells.filter((cell) => String(cell.class.id) === period.classId && onDay(cell.date, period.date));
  const group = groupPeriods(day).filter((item) => item.first <= period.index && period.index <= item.last)[0];
  return group ? periodGroupLabel(group.first, group.last) : periodGroupLabel(period.index);
}

/** Route state first, then the period's own timetable cell; '' for a label neither names. */
export function resolvePeriodLabels(state: Partial<PeriodLabels>, cells: QltTimetableDayCell[], period: BoardPeriod): PeriodLabels {
  const cell = state.subjectTitle && state.classTitle ? null : periodCell(cells, period);
  return {
    subjectTitle: state.subjectTitle || (cell ? cell.subject.title || cell.subject.name || '' : ''),
    classTitle: state.classTitle || (cell ? cell.class.title || '' : ''),
  };
}

export interface PeriodCourseScope {
  gradeId?: Id;
  subjectId?: Id;
}

function idOf(value: Id | { id: Id } | null | undefined): Id | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  return typeof value === 'object' ? value.id : value;
}

/** Grade and subject for the Kho bài tree query: route state first, then the period's own timetable cell. */
export function resolvePeriodCourseScope(state: PeriodCourseScope, cells: QltTimetableDayCell[], period: BoardPeriod): PeriodCourseScope {
  const cell = state.gradeId && state.subjectId ? null : periodCell(cells, period);
  return {
    gradeId: state.gradeId || (cell ? idOf(cell.class.grade) : undefined),
    subjectId: state.subjectId || (cell ? idOf(cell.subject) : undefined),
  };
}

/** "Lớp 7Test • Tiết 1-2 • Môn Toán 7" for a modal header; an unknown piece is left out with its separator. */
export function modalMeta(parts: { classTitle?: string; periodLabel?: string; subjectTitle?: string }): string {
  const pieces = [parts.classTitle ? `Lớp ${parts.classTitle}` : '', parts.periodLabel || '', parts.subjectTitle ? `Môn ${parts.subjectTitle}` : ''];
  return pieces.filter(Boolean).join(' • ');
}

/** "Lớp 6A6", or the neutral label when the name is unknown. */
export function classLabel(classTitle: string | undefined): string {
  return classTitle ? `Lớp ${classTitle}` : CLASS_FALLBACK;
}
